import dayjs from "dayjs";
import "dayjs/locale/it";
import cloneDeep from "lodash/cloneDeep";
import difference from "lodash/difference";
import router from "../../router";
import i18n from "../i18n";
import services from "../services";

const strict = true;

const state = {
  /**
   *  Appointment info after an appointment has been created
   */
  appointmentInfo: {},
  /**
   * Appointmentid after an appointment has been created
   */
  createdAppointmentId: "",
  /**
   * Index of current/shown step in wizard
   */
  currentStep: 1,
  /**
   * Value of the flow selcted by the "Vincoli" values: ["DOCTOR","OFFICE","DATE","NONE"]
   */
  bond: "",
  /**
   * The type of the future appointmet taken from the officeService
   */
  type: "",
  /**
   * A value that shows if the user is creating a reminder, so it would render different navigation steps
   */
  reminder: false,
  /**
   * A map that combines appointment type with step
   */
  typeMap: {
    SLOT: 11,
    TIMEPERIOD: 12,
    BYPHONE: 13
  },
  /**  --- B O O K I N G  W I Z A R D  S T E P S ---
   * id: 0 Beneficiary
   * id: 1 Service provider
   * id: 2 Province
   * id: 3 Network choice
   * id: 4 Service
   * id: 5 Diagnostic question
   * id: 6 Bookable months
   * id: 7 Constraints
   *   This is not a real step: it's included into step 6 if the selected service at step 5 requireObese or requireClaustrophobic
   * id: 8 Bond selection
   * id: 9 Office
   * id: 10 Doctor
   * id: 11 Calendar (SLOT/DATE bond)
   * id: 12 Portal date (PORTAL)
   * id: 13 Direct contact (BYPHONE)
   * id: 14 Summary
   * id: 15 Program a Reminder
   * id: 16 Feedback
   */

  /**
   * Step schema
   * id: Int: indexing functionality (unique)
   * name: String: Name of the step that is used in the route query
   * inMenu: Boolean: if true, it is shown in the navigation
   * completed: Boolean: if true, it has some info been selected in the step
   * active: Boolean: If true this step component is shown (unique)
   * visited: Boolean: If the step component has been visited once the value is set to true
   * title: String: String mapped for translation. The title on top of the page
   * menuTitle: String: String mapped for translation. The label in the navigation
   * selectedInfo: String: Info shown in the navigation for the info selected
   * icon: String:  The icon name that is shown in the navigation step
   * isValid: Callback: Checks if the info selected are valid and can proceed to next step
   * nextStep: Callback: @returns the next step index, accordingly to previous selected info (bond,type etc.)
   * prevStep: Callback: @returns the previous step index, accordingly to previous selected info (bond,type etc.)
   * info: Object: Contains info selected in that step
   * component: String: The component that is being rendered when step is active
   * searchResults: Array: The results that are returned from an autocomplete request
   * searchText: String: The input of the autocomplete search box is stored there
   * tooltip: Callback @returns the steps tooltip info in string
   * selectable: Callback @returns boolean if can be selected
   */
  steps: [
    {
      id: 0,
      completed: true,
      visited: true,
      active: false,
      menuTitle: "bookingWizard.menu.beneficiary",
      selectedInfo: "",
      icon: "person",
      info: {
        beneficiary: {},
        beneficiaries: []
      },
      component: "beneficiary"
    },
    {
      id: 1,
      name: "serviceArea",
      completed: false,
      visited: true,
      active: true,
      menuTitle: "bookingWizard.menu.service",
      selectedInfo: "",
      icon: "person",
      info: {
        beneficiaries: []
      },
      component: "service-selection"
    },
    {
      id: 2,
      name: "provincia",
      completed: false,
      visited: false,
      active: false,
      placeholderInstructions: "",
      selectedInfo: "",
      icon: "place",
      info: {
        province: {},
        provinces: []
      },
      component: "province"
    },
    {
      id: 3,
      name: "network",
      completed: false,
      visited: false,
      active: false,
      selectedInfo: "",
      searchText: "",
      info: {
        office: {},
        offices: []
      },
      component: "network"
    },
    {
      id: 4,
      name: "prestazione",
      completed: false,
      visited: false,
      active: false,
      menuTitle: "bookingWizard.menu.service",
      selectedInfo: "",
      icon: "widgets",
      searchResults: [],
      searchText: "",
      info: {
        service: {},
        services: [],
        residualCeiling: 0,
        exemption: 0,
        messages: [],
        bookableMonths: [],
        reminderMonths: [],
        bookableMonthsExcludingSanimpresa: [],
        sanimpresaBookableMonths: [],
        isSanimpresaMemberForBookableMonths: false,
        servicePrescription: "",
        selectedService: null
      },
      component: "service"
    },
    {
      id: 5,
      name: "questitoDiagnostica",
      completed: false,
      visited: false,
      active: false,
      menuTitle: "bookingWizard.menu.diagnosticQuestion",
      selectedInfo: "",
      icon: "contact_support",
      info: {
        pathology: "",
        constraint: {
          obesity: false,
          claustrophobia: false
        }
      },
      component: "service-diagnostic-question"
    },
    {
      id: 6,
      name: "mesiPrenotabili",
      completed: false,
      visited: false,
      active: false,
      menuTitle: "bookingWizard.menu.service",
      selectedInfo: "",
      icon: "widgets",
      info: {},
      component: "service-bookable-months"
    },
    // Is not a real step: it's included into step 5 if the selected service at step 4 requireObese or requireClaustrophobic
    {
      id: 7,
      name: "macchinario",
      completed: false,
      visited: false,
      active: false,
      menuTitle: "bookingWizard.menu.machinery",
      icon: "fas fa-laptop-medical",
      selectedInfo: "",
      info: {
        constraint: {
          obesity: false,
          claustrophobia: false
        }
      },
      component: "select-constraints"
    },
    {
      id: 8,
      name: "vincoli",
      completed: false,
      visited: false,
      active: false,
      menuTitle: "bookingWizard.menu.bond",
      selectedInfo: "",
      icon: "fas fa-bezier-curve",
      info: {},
      component: "appointment-configuration"
    },
    {
      id: 9,
      name: "struttura",
      completed: false,
      visited: false,
      active: false,
      menuTitle: "bookingWizard.menu.office",
      icon: "fas fa-clinic-medical",
      selectedInfo: "",
      searchResults: [],
      searchText: "",
      info: {
        office: {},
        officeServicePrice: 0,
        officeServiceExemption: 0,
        offices: [],
        officesByDate: {}
      },
      component: "select-structure"
    },
    {
      id: 10,
      name: "medico",
      completed: false,
      visited: false,
      active: false,
      menuTitle: "bookingWizard.menu.doctor",
      icon: "fas fa-stethoscope",
      selectedInfo: "",
      searchResults: [],
      searchText: "",
      info: {
        doctor: {},
        doctors: []
      },
      component: "select-doctor"
    },
    {
      id: 11,
      name: "calendario",
      completed: false,
      visited: false,
      active: false,
      menuTitle: "bookingWizard.menu.dateTime",
      icon: "fas fa-calendar-day",
      selectedInfo: "",
      info: {
        slots: [],
        slot: {},
        slotDuration: 0,
        slotOffset: 0,
        userSelectedDates: [],
        customSelectedDates: []
      },
      component: "calendar-view"
    },
    {
      id: 12,
      name: "portale",
      completed: false,
      visited: false,
      active: false,
      menuTitle: "bookingWizard.menu.dateTime",
      icon: "fas fa-calendar-day",
      selectedInfo: "",
      info: {
        timeTables: [],
        portalDays: {},
        portalDates: [],
        portalDatesAsText: ""
      },
      component: "portal-date-view"
    },
    {
      id: 13,
      name: "contattoDiretto",
      completed: false,
      visited: false,
      active: false,
      menuTitle: "bookingWizard.menu.dateTime",
      icon: "fas fa-calendar-day",
      selectedInfo: "",
      info: {},
      component: "direct-contact-view"
    },
    {
      id: 14,
      name: "riassunto",
      completed: false,
      visited: false,
      active: false,
      menuTitle: "bookingWizard.menu.summary",
      icon: "date_range",
      selectedInfo: "",
      info: {
        operationalService: {},
        voucherConfigurationType: "none",
        voucherConfiguration: {},
        userInfo: {},
        contactChannelChanged: false,
        sendVoucherModeChanged: false
      },
      component: "final-booking-recap"
    },
    {
      id: 15,
      name: "promemoria",
      completed: false,
      visited: true,
      active: false,
      menuTitle: "bookingWizard.reminderTitle",
      icon: "notifications",
      selectedInfo: "",
      info: {
        month: "",
        date: "",
        complete: false
      },
      component: "booking-reminder"
    },
    {
      id: 16,
      name: "feedback",
      completed: false,
      visited: false,
      active: false,
      menuTitle: "bookingWizard.reminderTitle",
      icon: "notifications",
      selectedInfo: "",
      info: {},
      component: "booking-feedback"
    }
  ],
  /**
   * The configuration for the TIMEPERIOD
   */
  morningTimeFrom: "07:00:00",
  splitTime: "14:30:00",
  afternoonTimeTo: "22:00:00",
  allPeriodSlots: [],
  /**
   * Other configurations to preload the calendar properly
   */
  initialMonthOffset: 0,
  firstBookableDate: dayjs().format("YYYY-MM-DD"),
  expostObject: null
};

const getters = {
  /**
   * @returns the current step object using the steps property
   */
  getCurrentStep: state => state.steps[state.currentStep],
  /**
   * @returns the index of the current step
   */
  getCurrentStepIndex: state => state.currentStep,
  /**
   * @returns the current step object using the flowSteps property
   */
  getCurrentFlowStep: state => flowSteps[state.currentStep],
  /**
   * @returns all the steps
   */
  getSteps: state => state.steps,
  /**
   * @param name the name of the step
   * @returns the step with the step name equals to name (used in router.js)
   */
  getStepByName: state => name => {
    return state.steps.find(step => step.name === name) || null;
  },
  /**
   * @returns the steps that have inMenu === true
   */
  getMenuSteps: state => {
    let filtered = [...flowSteps].filter(step => {
      if (flowSteps[state.currentStep].id === 15) {
        // promemoria
        let ids = [0, 4, 15];
        return ids.includes(step.id);
      } else {
        if (typeof step.inMenu === "function") {
          return step.inMenu(state);
        } else {
          return step.inMenu;
        }
      }
    });
    return [...state.steps].filter(el => {
      return filtered.some(f => {
        return f.id === el.id;
      });
    });
  },
  /**
   * @returns The beneficiary/user of the appointment
   */
  getBeneficiary: state => state.steps[0].info.beneficiary,
  getBeneficiaries: state => item =>
    [...state.steps[0].info.beneficiaries, item],
  /**
   * @returns the provinces
   */
  getProvinces: state => {
    let provinces = [...state.steps[2].info.provinces];
    provinces = sortByKey(provinces, "description");
    return provinces;
  },
  /**
   * @returns the selected province
   */
  getProvince: state => state.steps[2].info.province,
  /**
   * @returns the offices by province
   */
  getOfficesByProvince: state => state.steps[3].info.offices,
  /**
   * @returns the selected office by province
   */
  getOfficeByProvince: state => state.steps[3].info.office,
  /**
   * @param step
   * @returns the results after an autocomplete search in a specific step
   */
  getSearchResults: state => step => {
    return state.steps[step || state.currentStep].searchResults;
  },
  /**
   * @param step
   * @returns the search input after an autocomplete search in a specific step
   */
  getSearchText: state => step =>
    state.steps[step || state.currentStep].searchText || "",
  /**
   * @returns The services that where searched
   */
  getServices: state => state.steps[4].info.services,
  /**
   * @returns The service that has been selelcted
   */
  getService: state => state.steps[4].info.service,
  /**
   * @param {*} state
   * @returns the service prescription
   */
  getServicePrescription: state => state.steps[4].info.servicePrescription,
  /**
   * @param {*} state
   * @returns the service selected (param used by Service.vue to check if the service is selected )
   */
  getSelectedService: state => state.steps[4].info.selectedService,
  /**
   * @returns The service exemption
   */
  getExemption: state => state.steps[4].info.exemption,
  /**
   * @returns The residual ceiling of the service
   */
  getResidualCeiling: state => state.steps[4].info.residualCeiling,
  /**
   * @returns The available months for completing a booking
   */
  getBookableMonths: state => state.steps[4].info.bookableMonths,
  /**
   * @returns The available months for completing a reminder
   */
  getReminderMonths: state => state.steps[4].info.reminderMonths,
  /**
   * @returns The available months for completing a booking without considering pacchetto SANI
   */
  getBookableMonthsExcludingSanimpresa: state =>
    state.steps[4].info.bookableMonthsExcludingSanimpresa,
  /**
   * @returns The available months for completing a booking considering pacchetto SANI
   */
  getSanimpresaBookableMonths: state =>
    state.steps[4].info.sanimpresaBookableMonths,
  /**
   * @returns True if there are available months for completing a booking considering pacchetto SANI, otherwise false
   */
  isSanimpresaMemberForBookableMonths: state =>
    state.steps[4].info.sanimpresaBookableMonths.length > 0,
  /**
   * @returns The available boobkable months based on this logic:
   * - both for byportal and byslot, for no bond, doctor bond and office bond returns the boobkable months based on sanimpresa office value;
   * - for date bond, returns all the bookable months because the office has not been chosen yet
   */
  getBookableMonthsToUse: state => {
    let bond = state.bond;
    let office = state.steps[9].info.office;
    let isSanimpresaOffice = office?.sanimpresa || false;
    if (bond === "" || bond === "DOCOTR" || bond === "OFFICE") {
      if (isSanimpresaOffice) {
        return state.steps[4].info.sanimpresaBookableMonths;
      } else {
        return state.steps[4].info.bookableMonthsExcludingSanimpresa;
      }
    }
    return state.steps[4].info.bookableMonths;
  },
  /**
   * @returns The constraints that have been selected by the user if they are required
   */
  getConstraints: state => {
    let constraint = {};
    constraint["obese"] =
      state.steps[5].info.constraint.obese ||
      state.steps[5].info.constraint.obesity ||
      false;
    constraint["claustrophobia"] =
      state.steps[5].info.constraint.claustrophobia || false;
    return constraint;
  },
  /**
   * @returns the pathology completed from user in "Quesito Diagnostico"
   */
  getPathology: state => state.steps[5].info.pathology,
  /**
   * @returns the offices available for the service or searched
   */
  getOffices: state => state.steps[9].info.offices,
  /**
   * @returns the office selected or selected through the doctor
   */
  getOffice: state => state.steps[9].info.office,
  /**
   * @returns The office service price
   */
  getOfficeServicePrice: state => state.steps[9].info.officeServicePrice,
  /**
   * @returns The office service exemption
   */
  getOfficeServiceExemption: state =>
    state.steps[9].info.officeServiceExemption,
  /**
   * @returns the offices for a period of time
   */
  getOfficesByDate: state => state.steps[9].info.officesByDate,
  /**
   *  @returns the doctors available for a service or searched
   */
  getDoctors: state => state.steps[10].info.doctors,
  /**
   * @returns the doctor selected
   */
  getDoctor: state => state.steps[10].info.doctor,
  /**
   * @returns the user selected dates selected in "DATE" bond
   */
  getUserSelectedDates: state => state.steps[11].info.userSelectedDates,
  /**
   * @returns the custom formatted dates selected in "DATE" bond
   */
  getCustomSelectedDates: state => state.steps[11].info.customSelectedDates,
  /**
   * @returns the slots available for a service
   */
  getSlots: state => state.steps[11].info.slots,
  /**
   * @returns the slot selected
   */
  getSlot: state => state.steps[11].info.slot,
  /**
   * @returns the duration of the slots which is returned by the api
   */
  getSlotDuration: state => state.steps[11].info.slotDuration,
  /**
   * @returns the offset of the selected month from the current month
   */
  getSlotOffset: state => state.steps[11].info.slotOffset,
  getServiceTimetable: state => state.steps[12].info.timeTables,
  /**
   * @returns the selected days for a "TIMEPERIOD" appointment
   */
  getPortalDays: state => state.steps[12].info.portalDays,
  /**
   * @returns the selected dates for a "TIMEPERIOD" appointment
   */
  getPortalDates: state => state.steps[12].info.portalDates,
  /**
   * @returns the selected dates (formatted as text) for a "TIMEPERIOD" appointment
   */
  getPortalDatesAsText: state => state.steps[12].info.portalDatesAsText,
  /**
   * @returns The operational service of the selected service.This exists only if the service area equals to "Endoscopie"
   */
  getOperationalService: state => state.steps[14].info.operationalService,
  /**
   * @returns The messages that notify the user the situation of the coverages of the selected service
   */
  getServiceMessages: state => state.steps[4].info.messages,
  /**
   * @returns the voucher confiquration of the appointment
   */
  getVoucherConfiguration: state => state.steps[14].info.voucherConfiguration,
  /**
   * @returns the userInfo of the appointment
   */
  getUserInfo: state => state.steps[14].info.userInfo,
  /**
   * @returns true if the contact channel has changed, otherwise false
   */
  isContactChannelChanged: state => state.steps[14].info.contactChannelChanged,
  /**
   * @returns true if the contact channel has changed, otherwise false
   */
  isSendVoucherModeChanged: state =>
    state.steps[14].info.sendVoucherModeChanged,
  /**
   * @returns the result of the callback isValid of the current step
   */
  isValid: state => state.steps[state.currentStep].isValid(state),
  /**
   * @returns the bond of the appointment
   */
  getBond: state => state.bond,
  /**
   * @returns the months that is selected for a reminder
   */
  getReminderMonth: state => state.steps[15].info.month,
  /**
   * @returns the date that is selected for a reminder
   */
  getReminderDate: state => state.steps[15].info.date,
  /**
   * @returns the if the reminder flow is completed
   */
  getReminderComplete: state => state.steps[15].info.complete,
  getDocuments: state => state.steps[6].info.documents,
  /**
   * @returns the appointment type
   */
  getAppointmentType: state => state.type,
  /**
   * @returns the appointment id after its creation
   */
  getAppointmentInfo: state => state.appointmentInfo,
  /**
   * @returns the appointment id after its creation
   */
  getCreatedAppointmentId: state => state.createdAppointmentId,
  getMorningTimeFrom: state => state.morningTimeFrom,
  getSplitTime: state => state.splitTime,
  getAfternoonTimeTo: state => state.afternoonTimeTo,
  getAllPeriodSlots: state => state.allPeriodSlots,
  getPlaceholderInstructions: state =>
    state.steps[state.currentStep].placeholderInstructions,
  getInitialMonthOffset: state => state.initialMonthOffset,
  getFirstBookableDate: state => state.firstBookableDate,
  getExpostObject: state => state.expostObject
};

const flowSteps = [
  {
    id: 0,
    title: () => {
      return "bookingWizard.beneficiaryTitle";
    },
    inMenu: () => {
      return true;
    },
    isValid: state => {
      return state.steps[0].info.beneficiary.id;
    },
    nextStep: () => {
      return 1;
    },
    prevStep: () => {
      return 0;
    }
  },
  {
    id: 1,
    title: () => {
      return "bookingWizard.serviceSelection.title";
    },
    instructions: () => {
      return "bookingWizard.serviceSelection.instructions";
    },
    hasPlaceholderInstructions: () => {
      return false;
    },
    inMenu: () => {
      return false;
    },
    isValid: () => {
      return true;
    },
    nextStep: () => {
      return 2;
    },
    prevStep: () => {
      return 1;
    }
  },
  {
    id: 2,
    title: () => {
      return "bookingWizard.provinceTitle";
    },
    instructions: () => {
      return "bookingWizard.province.instructions";
    },
    hasPlaceholderInstructions: () => {
      return true;
    },
    inMenu: () => {
      return false;
    },
    isValid: state => {
      return state.steps[2].selectedInfo;
    },
    nextStep: state => {
      let isFullyCovered = state.steps[2].info.province.fullyCovered;
      if (isFullyCovered) {
        return 4;
      } else {
        return 3;
      }
    },
    prevStep: () => {
      return 1;
    }
  },
  {
    id: 3,
    title: () => {
      return "bookingWizard.networkTitle";
    },
    instructions: () => {
      return "bookingWizard.network.instructions";
    },
    hasPlaceholderInstructions: () => {
      return false;
    },
    inMenu: () => {
      return false;
    },
    isValid: state => {
      return state.steps[3].info.office;
    },
    nextStep: () => {
      return 4;
    },
    prevStep: () => {
      return 2;
    }
  },
  {
    id: 4,
    title: () => {
      return "bookingWizard.serviceTitle";
    },
    instructions: () => {
      return "bookingWizard.service.instructions";
    },
    hasPlaceholderInstructions: () => {
      return false;
    },
    inMenu: () => {
      return true;
    },
    isValid: state => {
      return (
        state.steps[4].info.service.serviceId &&
        state.steps[4].info.residualCeiling
      );
    },
    nextStep: () => {
      return 6;
    },
    prevStep: state => {
      let isFullyCovered = state.steps[2].info.province.fullyCovered;
      if (isFullyCovered) {
        return 2;
      } else {
        return 3;
      }
    }
  },
  {
    id: 5,
    title: () => {
      return "bookingWizard.pathologyTitle";
    },
    instructions: () => {
      return "bookingWizard.pathology.instructions";
    },
    hasPlaceholderInstructions: () => {
      return false;
    },
    inMenu: () => {
      return true;
    },
    isValid: state => {
      return state.steps[5].info.pathology;
    },
    nextStep: () => {
      return 8;
    },
    prevStep: () => {
      return 6;
    }
  },
  {
    id: 6,
    title: () => {
      return "bookingWizard.bookableMonthsTitle";
    },
    instructions: () => {
      return "bookingWizard.bookableMonths.instructions";
    },
    hasPlaceholderInstructions: () => {
      return false;
    },
    inMenu: () => {
      return false;
    },
    isValid: () => {
      return true;
    },
    nextStep: () => {
      return 5;
    },
    prevStep: () => {
      return 4;
    }
  },
  // Is not a real step: it's included into step 5 if the selected service at step 4 requireObese or requireClaustrophobic
  {
    id: 7,
    title: () => {
      return "bookingWizard.macchinarioTitle";
    },
    instructions: () => {
      return "bookingWizard.machinery.instructions";
    },
    hasPlaceholderInstructions: () => {
      return false;
    },
    inMenu: () => {
      return false;
    },
    isValid: () => {
      return true;
    },
    nextStep: () => {
      return 8;
    },
    prevStep: () => {
      return 5;
    }
  },
  {
    id: 8,
    title: () => {
      return "bookingWizard.bondTitle";
    },
    instructions: () => {
      return "bookingWizard.bondSelection.instructions";
    },
    hasPlaceholderInstructions: () => {
      return false;
    },
    inMenu: () => {
      return true;
    },
    isValid: () => {
      return true;
    },
    nextStep: state => {
      switch (state.bond) {
        case "DOCTOR":
          return 10;
        case "DATE":
          return 11;
        default:
          return 9;
      }
    },
    prevStep: () => {
      return 5;
    }
  },
  {
    id: 9,
    title: () => {
      return "bookingWizard.officeTitle";
    },
    instructions: bond => {
      if (!bond) {
        // should be only the search by ranking
        return "bookingWizard.office.bestInstructions";
      } else {
        // should be for all the BOND types
        return "bookingWizard.office.instructions";
      }
    },
    hasPlaceholderInstructions: () => {
      return false;
    },
    inMenu: () => {
      return true;
    },
    isValid: state => {
      return state.steps[9].info.office.officeId;
    },
    nextStep: state => {
      let bookingType = state.steps[9].info.office.bookingType;
      let vsp = state.steps[4].info.service.macroArea === "VSP";
      if (state.bond === "DATE") {
        return vsp ? 10 : 14;
      } else {
        // should be only with BOND == "OFFICE"
        return vsp ? 10 : state.typeMap[bookingType];
      }
    },
    prevStep: state => {
      if (state.bond === "DATE") {
        return 11;
      }
      return 8;
    }
  },
  {
    id: 10,
    title: () => {
      return "bookingWizard.doctorsTitle";
    },
    instructions: () => {
      return "bookingWizard.doctorSelection.instructions";
    },
    hasPlaceholderInstructions: () => {
      return false;
    },
    inMenu: state => {
      let service = state.steps[4].info.service;
      return service && service.macroArea === "VSP";
    },
    isValid: () => {
      return true;
    },
    nextStep: state => {
      let bookingType = state.steps[9].info.office.bookingType;
      return state.bond === "DATE" ? 14 : state.typeMap[bookingType];
    },
    prevStep: state => {
      return state.bond === "DOCTOR" ? 8 : 9;
    }
  },
  {
    id: 11,
    title: () => {
      return "bookingWizard.dateTimeTitle";
    },
    instructions: () => {
      return "bookingWizard.dateSelection.calendarInstructions";
    },
    hasPlaceholderInstructions: () => {
      return false;
    },
    inMenu: state => {
      let office = state.steps[9].info.office;
      return office && office.bookingType === "SLOT";
    },
    isValid: state => {
      let dateTime = state.steps[11].info.slot.dateTime;
      let customDate = state.steps[11].info.date;
      if (state.bond !== "DATE" && state.type === "SLOT" && dateTime) {
        return true;
      }
      return state.bond === "DATE" && customDate;
    },
    nextStep: state => {
      if (state.bond === "DATE") {
        return 9;
      }
      return 14;
    },
    prevStep: state => {
      if (state.bond === "DATE") {
        return 8;
      }
      return state.steps[4].info.service.macroArea === "VSP" ? 10 : 9;
    }
  },
  {
    id: 12,
    title: () => {
      return "bookingWizard.dateTimeTitle";
    },
    instructions: () => {
      return "bookingWizard.dateSelection.portalInstructions";
    },
    hasPlaceholderInstructions: () => {
      return false;
    },
    inMenu: state => {
      let office = state.steps[9].info.office;
      return office && office.bookingType === "TIMEPERIOD";
    },
    isValid: state => {
      return state.steps[12].info.portalDays.find(day => day.checked);
    },
    nextStep: () => {
      return 14;
    },
    prevStep: state => {
      return state.steps[4].info.service.macroArea === "VSP" ? 10 : 9;
    }
  },
  {
    id: 13,
    title: () => {
      return "bookingWizard.dateTimeTitle";
    },
    inMenu: state => {
      let office = state.steps[9].info.office;
      return office && office.bookingType === "BYPHONE";
    },
    isValid: () => {
      return true;
    },
    nextStep: () => {
      return 14;
    },
    prevStep: state => {
      return state.steps[4].info.service.macroArea === "VSP" ? 10 : 9;
    }
  },
  {
    id: 14,
    title: appointmentType => {
      if (appointmentType === "TIMEPERIOD") {
        // should be only for the BY PORTAL
        return "bookingWizard.portalSummary";
      } else {
        // should be only for the BY SLOT
        return "bookingWizard.slotSummary";
      }
    },
    inMenu: () => {
      return true;
    },
    isValid: () => {
      return true;
    },
    nextStep: () => 16,
    prevStep: state => {
      let bookingType = state.steps[9].info.office.bookingType;
      if (state.bond === "DATE" && bookingType === "SLOT") {
        return state.steps[4].info.service.macroArea === "VSP" ? 10 : 9;
      } else {
        return state.typeMap[bookingType];
      }
    }
  },
  {
    id: 15,
    title: () => {
      return "bookingWizard.reminderTitle";
    },
    inMenu: () => {
      return false;
    },
    isValid: state => {
      return (
        state.steps[4].info.service.serviceId &&
        state.steps[9].info.office.officeId &&
        state.steps[15].info.date
      );
    },
    nextStep: () => {},
    prevStep: () => 6
  },
  {
    id: 16,
    title: () => {
      return "bookingWizard.feedbackTitle";
    },
    inMenu: () => {
      return false;
    },
    isValid: () => true,
    nextStep: () => {},
    prevStep: () => {}
  }
];

const mutations = {
  /**
   * @param {*} state
   * Sets the next step of the wizard calling the nextStep callback of the current step and setting the router
   */
  nextStep(state) {
    // 1. set properties of the completed step
    state.steps[state.currentStep].active = false;
    state.steps[state.currentStep].completed = true;
    // 1.1 for DOCTOR bond, if the completed step is the medico step, mark also the struttura step as completed
    if (state.bond === "DOCTOR" && state.steps[state.currentStep].id === 10) {
      state.steps[9].active = false;
      state.steps[9].visited = true;
      state.steps[9].completed = true;
    }
    // 1.2 for DATE bond, if the completed step is the struttura step, mark also the calendario step as completed
    if (state.bond === "DATE" && state.steps[state.currentStep].id === 9) {
      state.steps[11].active = false;
      state.steps[11].visited = true;
      state.steps[11].completed = true;
    }
    // 2. get the next step
    state.currentStep = flowSteps[state.currentStep].nextStep(state);
    // 3. set properties of the next step
    state.steps[state.currentStep].active = true;
    state.steps[state.currentStep].visited = true;
    state.steps[state.currentStep].completed = false;
    // 4. go to step
    router.push({
      name: "bookingWizard",
      query: { q: state.steps[state.currentStep].name }
    });
  },
  /**
   * @param {*} state
   * Sets the previous step of the wizard calling the prevStep callback of the current step and setting the router
   */
  async previousStep(state) {
    // 1. reset properties of the leaving step
    resetSteps(
      state.steps,
      state.currentStep,
      state.currentStep + 1,
      state.bond,
      "PREVIOUS"
    );
    // 2. get the next step
    state.currentStep = flowSteps[state.currentStep].prevStep(state);
    // 3. set properties of the next step
    state.steps[state.currentStep].active = true;
    state.steps[state.currentStep].visited = true;
    state.steps[state.currentStep].completed = false;
    // 4. go to step
    router.go(-1);
  },
  /**
   * @param {*} state
   * @param {*} id
   * Goes to the step with id (Called from router.js)
   */
  editStep(state, id) {
    // 1. reset properties of the leaving step (should be always riassunto step)
    resetSteps(
      state.steps,
      state.currentStep,
      state.currentStep + 1,
      state.bond,
      "EDIT"
    );
    // 2. get the next step based on the requested id of the step to edit
    state.currentStep = state.steps.findIndex(step => step.id === id);
    // 3. set properties of the next step
    state.steps[state.currentStep].active = true;
    state.steps[state.currentStep].visited = true;
    state.steps[state.currentStep].completed = false;
    // 4. reset all the steps starting from the new current step plus 1
    resetStepsForEditAction(state.steps, state.currentStep + 1, null);
    // 5. go to step
    router.push({
      name: "bookingWizard",
      query: { q: state.steps[state.currentStep].name }
    });
  },
  /**
   * @param {*} state
   * @param {*} item
   * Sets the beneficiary/user of the appointment
   */
  beneficiary(state, item) {
    let fullName = item.nome.concat(" ").concat(item.cognome);
    state.steps[0].info.beneficiary = item;
    state.steps[0].selectedInfo = fullName;
    state.steps[0].visited = true;
    state.steps[0].completed = true;
  },
  beneficiaries(state, beneficiaries) {
    state.steps[0].beneficiaries = beneficiaries;
  },
  /**
   * @param {*} state
   * @param {*} provinces
   * Sets the provinces
   */
  provinces(state, provinces) {
    state.steps[2].info.provinces = provinces;
  },
  /**
   * @param {*} state
   * @param {*} province
   * Sets the province selected
   */
  province(state, province) {
    state.steps[2].info.province = province;
    state.steps[2].selectedInfo = state.steps[2].info.province.description;
  },
  /**
   * @param {*} state
   * @param {*} offices
   * Sets the offices by province
   */
  officesByProvince(state, offices) {
    state.steps[3].info.offices = offices;
  },
  /**
   * @param {*} state
   * @param {*} office
   * Sets the office selected
   */
  officeByProvince(state, office) {
    state.steps[3].info.office = office;
    state.steps[3].selectedInfo = office ? office.legalName : null;
  },
  /**
   * @param {*} state
   * @param {*} input
   * Sets the search input of the autocomplete
   */
  searchText(state, input) {
    state.steps[state.currentStep].searchText = input;
  },
  /**
   * @param {*} state
   * @param {*} results
   * @param {*} step
   *  Sets the results after an autocomplete. If step otherwise sets the current step's searchResults
   */
  searchResults(state, { results, step }) {
    state.steps[step || state.currentStep].searchResults = results;
  },
  /**
   * @param {*} state
   * @param {*} service
   * Sets the service selected
   */
  service(state, { service }) {
    state.steps[4].info.service = service;
  },
  /**
   * @param {*} state
   * set the selected info adding service name and servicePrescription
   */
  serviceSelectedInfo(state) {
    let service = state.steps[4].info.service;
    let servicePrescription = state.steps[4].info.servicePrescription;

    let serviceName = service
      ? service.label?.defaultValue || service.nameByLang || ""
      : "";

    let sn = `Prestazione richiesta: ${serviceName}`;
    let sp = servicePrescription
      ? `Prestazione in prescrizione: ${servicePrescription.toUpperCase()}`
      : null;

    let info = [sn, sp].filter(Boolean);
    let formattedInfo = info.join("</br>");
    state.steps[4].selectedInfo = formattedInfo;
  },
  /**
   * @param {*} state
   * @param {*} service
   * set the selectedService param
   */
  selectedService(state, service) {
    state.steps[4].info.selectedService = service;
  },
  /**
   * @param {*} state
   * @param {*} value
   * set the servicePrescription
   */
  servicePrescription(state, value) {
    state.steps[4].info.servicePrescription = value;
  },
  /**
   *
   * @param {*} state
   * @param {*} services
   * Sets the services searched
   */
  services(state, services) {
    state.steps[4].info.services = services;
  },
  /**
   *
   * @param {*} state
   * @param {*} messages
   * Sets the messages that notify the user for his coverage
   */
  messages(state, messages) {
    state.steps[4].info.messages = messages;
  },
  /**
   * @param {*} state
   * @param {*} months
   * Sets the available months for booking
   */
  bookableMonths(state, months) {
    state.steps[4].info.bookableMonths = months;
  },
  /**
   * @param {*} state
   * @param {*} months
   * Sets the available months for booking
   */
  reminderMonths(state, months) {
    state.steps[4].info.reminderMonths = months;
  },
  /**
   * @param {*} state
   * @param {*} months
   * Sets the available months for booking without considering the months with pacchetto SANI
   */
  bookableMonthsExcludingSanimpresa(state, months) {
    state.steps[4].info.bookableMonthsExcludingSanimpresa = months;
  },
  /**
   * @param {*} state
   * @param {*} months
   * Sets the available months for booking considering only months with pacchetto SANI
   */
  sanimpresaBookableMonths(state, months) {
    state.steps[4].info.sanimpresaBookableMonths = months;
    state.steps[4].info.isSanimpresaMemberForBookableMonths = months.length > 0;
  },
  /**
   * @param {*} state
   * @param {*} description
   * Sets the pathology added by the user
   */
  pathologyDescription(state, description) {
    state.steps[5].info.pathology = description;
    state.steps[5].selectedInfo = description;
  },
  /**
   * @param {*} state
   * @param {*} constraint
   * Sets the constraint added by the user
   */
  constraints(state, constraint) {
    state.steps[5].info.constraint = constraint;
  },
  /**
   * @param {*} state
   * @param {*} office
   * Sets the office selected by the user
   */
  office(state, office) {
    state.steps[9].info.office = office;
    if (office.officeId) {
      let t = i18n ? i18n.t("office") : "Struttura Sanitaria";
      state.steps[9].selectedInfo = t.concat(": ").concat(office.legalName);
      if (office.nestCalendar) {
        state.steps[11].info.slots = office.nestCalendar.servicePeriodSlot;
      }
      state.steps[9].info.officeServicePrice = office?.servicePrice
        ? office.servicePrice.slice(4, -1)
        : 0;
      state.steps[9].info.officeServiceExemption = office?.serviceExemption
        ? office.serviceExemption.slice(4, -1)
        : 0;
      state.type = office.bookingType;
    }
  },
  /**
   * @param {*} state
   * @param {*} results
   * Sets the offices available for the selected service
   */
  offices(state, results) {
    state.steps[9].info.offices = results;
  },
  /**
   * @param {*} state
   * @param {*} officesMap
   * Sets the offices available for the selected service in a period of time
   */
  officesByDate(state, officesMap) {
    state.steps[9].info.officesByDate = officesMap;
  },
  /**
   * @param {*} state
   * @param {*} doctor
   * Sets the doctor selected for the service
   */
  doctor(state, doctor) {
    let d = doctor;
    if (Object.keys(doctor).length === 0) {
      d.doctorName = "nessuna";
      d.doctorSurName = "preferenza";
    }
    state.steps[10].info.doctor = d;
    let t = i18n ? i18n.t("doctor") : "Medico";
    state.steps[10].selectedInfo = t
      .concat(": ")
      .concat(`${d.doctorName} ${d.doctorSurName}`);
    if (state.bond === "DOCTOR") {
      let t = i18n ? i18n.t("office") : "Struttura Sanitaria";
      state.steps[9].selectedInfo = t.concat(": ").concat(d.officeName);
    }
  },
  /**
   * @param {*} state
   * @param {*} results
   *  Sets the doctors available for the service or for an office
   */
  doctors(state, results) {
    state.steps[10].info.doctors = results;
  },
  /**
   * @param {*} state
   * @param {*} slots
   * Sets the slots of the service
   */
  slots(state, slots) {
    state.steps[11].info.slots = slots;
  },
  /**
   * @param {*} state
   * @param {*} slot
   * Sets the slot selected
   */
  slot(state, slot) {
    state.steps[11].info.slot = slot;
    let dateTime = slot.dateTime;
    state.steps[11].selectedInfo = `${dayjs(dateTime).format(
      "dddd DD MMMM"
    )} ${i18n.t("toTime")} ${dayjs(dateTime).format("HH:mm")}`;
  },
  /**
   * @param {*} state
   * @param {*} duration
   * Sets the slot duration
   */
  slotDuration(state, duration) {
    state.steps[11].info.slotDuration = duration;
  },
  /**
   * @param {*} state
   * @param {*} slotOffset
   * Sets the slot offset
   */
  slotOffset(state, slotOffset) {
    state.steps[11].info.slotOffset = slotOffset;
  },
  /**
   * @param {*} state
   * @param {*} dates
   * Sets the selected dates in DATE bond
   */
  userSelectedDates(state, dates) {
    state.steps[11].info.userSelectedDates = dates;
  },
  /**
   * @param {*} state
   * @param {*} dates
   * Sets the formatted dates in DATE bond
   */
  customSelectedDates(state, dates) {
    state.steps[11].info.customSelectedDates = dates;
  },
  setServiceTimetable(state) {
    let serviceTT = [
      ...state.steps[9].info.office.nestCalendar.serviceTimeTable
    ];
    let bond = state.bond;
    if (bond === "DOCTOR") {
      let doctorId = state.steps[10].info.doctor.doctorId;
      serviceTT = serviceTT.filter(stt => {
        return stt.doctorId == doctorId;
      });
    }
    serviceTT = serviceTT.reduce((a, b) => {
      if (!a[b.day]) a[b.day] = [];
      let [startHH, startMM] = b.fromHHMM.split(":");
      let newStartMM = null;
      if (Number(startMM) > 0 && Number(startMM) < 30) {
        newStartMM = "00";
      } else if (Number(startMM) > 30 && Number(startMM) <= 59) {
        newStartMM = "30";
      } else {
        newStartMM = startMM;
      }
      let newFrom = [startHH, newStartMM].join(":");
      b["newFromHHMM"] = newFrom;
      let [endHH, endMM] = b.toHHMM.split(":");
      let newEndHH = null;
      let newEndMM = null;
      if (Number(endMM) > 0 && Number(endMM) < 30) {
        newEndHH = endHH;
        newEndMM = "30";
      } else if (Number(endMM) > 30 && Number(endMM) <= 59) {
        newEndHH = Number(endHH) + Number(1);
        newEndMM = "00";
      } else {
        newEndHH = endHH;
        newEndMM = endMM;
      }
      let newTo = [newEndHH, newEndMM].join(":");
      b["newToHHMM"] = newTo;
      [startHH, startMM] = b.newFromHHMM.split(":");
      let startDate = dayjs()
        .set("hour", Number(startHH))
        .set("minute", Number(startMM));
      [endHH, endMM] = b.newToHHMM.split(":");
      let endDate = dayjs()
        .set("hour", Number(endHH))
        .set("minute", Number(endMM))
        .subtract(29, "minute");
      let slots = [];
      while (startDate.isBefore(endDate)) {
        let newDate = startDate.add(30, "minute");
        slots.push(`${startDate.format("HH:mm")} - ${newDate.format("HH:mm")}`);
        startDate = newDate;
      }
      b["slots"] = slots;
      a[b.day].push(b);
      return a;
    }, {});
    state.steps[12].info.timeTables = serviceTT;
  },
  /**
   * Sets the days for a DOCTOR or OFFICE bond in a TIMEPERIOD appointment
   */
  setPortalDays(state, { newDates, dates }) {
    let portalDays = { ...state.steps[12].info.portalDays };
    let result = {};
    if (newDates.length < dates.length) {
      let diffDates = difference(dates, newDates);
      result = diffDates.reduce((a, b) => {
        let key = dayjs(b).format("MMMM YYYY - dddd");
        let idx = a[key].findIndex(d => d.date === b);
        if (idx > -1) {
          a[key].splice(idx, 1);
        }
        if (!a[key].length) {
          delete a[key];
        } else {
          a[key].sort((c, d) =>
            dayjs(c.date).isAfter(dayjs(d.date)) ? 1 : -1
          );
        }
        return a;
      }, portalDays);
    } else {
      let timeTables = state.steps[12].info.timeTables;
      let diffDates = difference(newDates, dates);
      result = diffDates.reduce((a, b) => {
        let key = dayjs(b).format("MMMM YYYY - dddd");
        if (!a[key]) a[key] = [];
        let day = dayjs(b).locale("en").format("dddd").toUpperCase();
        let newDate = {
          day,
          date: b,
          timetables: timeTables[day] ? cloneDeep(timeTables[day]) : []
        };
        a[key].push(newDate);
        a[key].sort((c, d) => (dayjs(c.date).isAfter(dayjs(d.date)) ? 1 : -1));
        return a;
      }, portalDays);
    }
    state.steps[12].info.portalDates = newDates;
    state.steps[12].info.portalDays = result;
  },
  /**
   * Build the portal days info used into the timeline tooltip for a DOCTOR or OFFICE bond in a TIMEPERIOD appointment
   */
  buildPortalDaysTooltipInfo(state) {
    let mappedDates = { ...state.steps[12].info.portalDays };
    let requestedDates = getRequestedDatesFromPortalDays(mappedDates);
    let grouppedByDate = requestedDatesGrouppedByDate(requestedDates);
    let compressed = compressedRequestedDates(grouppedByDate);
    let dates = compressedAsText(compressed);
    state.steps[12].info.portalDatesAsText = dates;
    state.steps[12].selectedInfo = dates;
  },
  updateDateSlots(state, { period, selectedSlots }) {
    let portalDays = { ...state.steps[12].info.portalDays };
    let key = dayjs(period.date).format("MMMM YYYY - dddd");
    let p = portalDays[key].find(md => md.date === period.date);
    if (p) {
      p.timetables.forEach(function (tt) {
        if (tt && !tt.orignalSlots) {
          tt["orignalSlots"] = tt.slots;
        }
        let newSlots = [];
        tt.orignalSlots.forEach(function (s) {
          let found = selectedSlots.find(slot => slot.range === s);
          if (found !== undefined) {
            newSlots.push(s);
          }
        });
        if (newSlots.length > 0) {
          tt.slots = newSlots;
          tt["changed"] = true;
        } else {
          tt["changed"] = false;
        }
      });
    }
    state.steps[12].info.portalDays = portalDays;
  },
  updateWeekDaySlots(state, { period, selectedSlots }) {
    let portalDays = { ...state.steps[12].info.portalDays };
    let weekday = dayjs(period.date).locale("it").format("dddd");
    let weekdayKeys = Object.keys(portalDays).filter(mdKey =>
      mdKey.includes(weekday)
    );
    weekdayKeys.forEach(wdKey => {
      portalDays[wdKey].forEach(md => {
        md.timetables.forEach(function (tt) {
          if (tt && !tt.orignalSlots) {
            tt["orignalSlots"] = tt.slots;
          }
          let newSlots = [];
          tt.orignalSlots.forEach(function (s) {
            let found = selectedSlots.find(slot => slot.range === s);
            if (found !== undefined) {
              newSlots.push(s);
            }
          });
          if (newSlots.length > 0) {
            tt.slots = newSlots;
            tt["changed"] = true;
          } else {
            tt["changed"] = false;
          }
        });
      });
    });
    state.steps[12].info.portalDays = portalDays;
  },
  /**
   *
   * @param {*} state
   * @param {*} t
   * Sets the type of the appointment
   */
  type(state, t) {
    state.type = t;
  },
  /**
   * @param {*} state
   * @param {*} ceiling
   * Sets the recidual ceiling of the service
   */
  residualCeiling(state, ceiling) {
    state.steps[4].info.residualCeiling = ceiling;
  },
  /**
   * @param {*} state
   * @param {*} exemption
   * Sets the exemption of the service
   */
  exemption(state, exemption) {
    state.steps[4].info.exemption = exemption;
  },
  /**
   * @param {*} state
   * @param {*} service
   * Sets the operational service if any
   */
  operationalService(state, service) {
    state.steps[14].info.operationalService = service;
  },
  /**
   * @param {*} state
   * Sets the configuration of a voucher
   */
  voucherConfiguration(state, value) {
    state.steps[14].info.voucherConfiguration = value;
  },
  /**
   * @param {*} state
   * Sets the userInfo of a voucher
   */
  userInfo(state, value) {
    state.steps[14].info.userInfo = value;
  },
  /**
   * Sets the value (true/false) for contactChannelChanged
   */
  setContactChannelChanged(state, value) {
    state.steps[14].info.contactChannelChanged = value;
  },
  /**
   * Sets the value (true/false) for sendVoucherModeChanged
   */
  setSendVoucherModeChanged(state, value) {
    state.steps[14].info.sendVoucherModeChanged = value;
  },
  /**
   * @param {*} state
   * @param {*} bond
   * Sets the bond of the wizard
   */
  bond(state, bond) {
    state.bond = bond;
    if (bond === "OFFICE") {
      state.steps[8].selectedInfo = i18n
        ? i18n.t("bookingWizard.bondSelection.bondInfoForOffice")
        : "Vincolo per ragione sociale";
    } else if (bond === "DATE") {
      state.steps[8].selectedInfo = i18n
        ? i18n.t("bookingWizard.bondSelection.bondInfoForAgenda")
        : "Vincolo per giorno/orario specifico";
    } else if (bond === "DOCTOR") {
      state.steps[8].selectedInfo = i18n
        ? i18n.t("bookingWizard.bondSelection.bondInfoForDoctor")
        : "Vincolo per nome del medico";
    } else {
      state.steps[8].selectedInfo = i18n
        ? i18n.t("bookingWizard.bondSelection.noBondInfo")
        : "Nessun vincolo";
    }
  },
  /**
   * @param {*} state
   * @param {*} val
   * Sets the the value of the reminder
   */
  isReminder(state, val) {
    state.reminder = val;
  },
  /**
   * @param {*} state
   * @param {*} month
   * Sets the reminder month
   */
  reminderMonth(state, month) {
    state.steps[15].info.month = month;
  },
  /**
   * @param {*} state
   * Sets the reminder as completed
   */
  setReminderComplete(state) {
    state.steps[15].info.complete = true;
  },
  /**
   * @param {*} state
   * @param {*} date
   * Sets the reminder date
   */
  reminderDate(state, date) {
    state.steps[15].info.date = date;
  },
  /**
   * @param {*} state
   * @param {*} id
   * Sets the appointment info after its creation
   */
  appointmentInfo(state, info) {
    state.appointmentInfo = info;
  },
  /**
   * @param {*} state
   * @param {*} id
   * Sets the appointment id after its creation
   */
  appointmentId(state, id) {
    state.createdAppointmentId = id;
  },
  /**
   * @param {*} state
   * Resets the state and recurrent steps
   */
  async reset(state) {
    state.createdAppointmentId = "";
    state.currentStep = 1;
    state.bond = "";
    state.type = "";
    resetSteps(state.steps);
    state.steps[1].active = true;
    state.steps[1].visited = true;
  },
  resetAllPeriodSlots(state) {
    let slots = [];
    let [startHH, startMM, startSS] = state.morningTimeFrom.split(":");
    let startDate = dayjs()
      .set("hour", Number(startHH))
      .set("minute", Number(startMM))
      .set("second", Number(startSS));
    let [endHH, endMM, endSS] = state.afternoonTimeTo.split(":");
    let endDate = dayjs()
      .set("hour", Number(endHH))
      .set("minute", Number(endMM))
      .set("second", Number(endSS));
    while (startDate.isBefore(endDate)) {
      let newDate = startDate.add(30, "minute");
      slots.push({
        range: `${startDate.format("HH:mm")} - ${newDate.format("HH:mm")}`,
        disabled: true
      });
      startDate = newDate;
    }
    if (slots.length === 31) {
      slots.pop();
    }
    state.allPeriodSlots = slots;
  },
  setCoveredAndBookableProvincesDescriptions(state, provincesDescriptions) {
    state.steps[2].placeholderInstructions = provincesDescriptions;
  },
  setCalendarConf(state, bookableMonthsToUse) {
    let firstBookableMonth = bookableMonthsToUse[0];
    let firstBookableDate = dayjs(
      `${firstBookableMonth.year}-0${firstBookableMonth.month}-01`
    );
    let monthOffset = firstBookableDate.diff(
      dayjs().date(1).format("YYYY-MM-DD"),
      "month"
    );
    state.initialMonthOffset = monthOffset;
    if (monthOffset == 0) {
      state.firstBookableDate = dayjs().format("YYYY-MM-DD");
    } else {
      state.firstBookableDate = dayjs(firstBookableDate).format("YYYY-MM-DD");
    }
  },
  setExpostObject(state, bookableMonthsToUse) {
    let bookableMonths = [...bookableMonthsToUse].map(coverage => ({
      ...coverage,
      covered:
        !coverage.notCovered &&
        coverage.bookableByRule &&
        !coverage.maxCeilingReached &&
        !coverage.maxHealthCareReached
    }));
    state.expostObject = {
      province: state.steps[2].info.province,
      service: state.steps[4].info.service,
      pathology: state.steps[5].info.pathology,
      office: state.steps[9].info.office,
      bookableMonths: bookableMonths
    };
  }
};

const actions = {
  /**
   * Get the descriptions of the only covered and bookable provinces
   */
  async getCoveredAndBookableProvincesDescriptions({ commit }) {
    try {
      commit("loading/openLoadingDialog", null, {
        root: true
      });
      let result =
        await services.bookingsService.callGetCoveredAndBookableProvincesDescriptions();
      commit("setCoveredAndBookableProvincesDescriptions", result);
      commit("loading/closeLoadingDialog", null, {
        root: true
      });
    } catch (reason) {
      commit("loading/closeLoadingDialog", null, {
        root: true
      });
      commit("error/setError", reason, { root: true });
      throw reason;
    }
  },
  /**
   * Gets and commits the provinces returned by the service
   */
  async getAllProvinces({ commit, rootGetters }) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let provinces = rootGetters["profile/allProvinces"];
      if (provinces && provinces.length === 0) {
        provinces = await services.bookingsService.getAllProvinces();
      }
      commit("provinces", provinces);
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  /**
   * Gets and commits the offices by province returned by the service
   */
  async getOfficesByProvince({ commit, getters, rootGetters }, toSearch) {
    try {
      commit("loading/openLoadingDialog", null, {
        root: true
      });
      let searchQuery = toSearch ? toSearch.replace(/([^-]* - )/g, "") : "";
      let provinceCode = getters["getProvince"].code;
      let isSanimpresaMember =
        rootGetters["coverages/isSanimpresaMemberForCoverage"] || false;
      let payload = {
        toSearch: searchQuery,
        provinceCode: provinceCode,
        isSanimpresaMember: isSanimpresaMember
      };
      let offices = await services.bookingsService.getOfficesByProvince(
        payload
      );
      offices = sortByKey(offices, "legalName");
      commit(
        "officesByProvince",
        applyOfficesOrder(offices, isSanimpresaMember)
      );
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async setOfficeByProvince({ commit }, office) {
    commit("officeByProvince", office);
    commit("nextStep");
  },
  /**
   * @param {*} search
   * Gets the services after a search.
   */
  async getServices({ getters, commit, rootGetters }, search) {
    try {
      let searchQuery = search.searchQuery
        ? search.searchQuery.replace(/([^-]* - )/g, "")
        : "";
      let isSanimpresaMember =
        rootGetters["coverages/isSanimpresaMemberForCoverage"] || false;
      if (searchQuery.length >= 3) {
        let serviceList = await services.bookingsService.getServiceNames({
          search: searchQuery || "",
          provinceCode: getters["getProvince"].code,
          includeAliases: false,
          isSanimpresaMember: isSanimpresaMember
        });
        // Sorts the services by name
        serviceList = sortByKey(serviceList, "nameByLang");
        // Sets the searchResults of the step
        commit("searchResults", { results: serviceList, step: 4 });
        // If not autocomplete it returns the
        if (!search.autocomplete) {
          commit("services", serviceList);
        }
      } else {
        // Sets empty for searchResults of the step
        commit("searchResults", { results: [], step: 4 });
      }
    } catch (error) {
      commit("error/setError", error, { root: true });
    }
  },
  /**
   * @param {*} search
   * Gets the doctors according to search input
   */
  async getDoctors({ commit, getters }, search) {
    try {
      // Finds the office name so we can filter the results at the end
      let seperatorIdx = search.searchQuery
        ? search.searchQuery.indexOf("-")
        : -1;
      let searchedOfficeName =
        seperatorIdx > -1
          ? search.searchQuery.substring(
              seperatorIdx + 2,
              search.searchQuery.length
            )
          : "";
      // Sets the search query without the office name
      search.searchQuery =
        seperatorIdx > -1
          ? search.searchQuery.slice(0, seperatorIdx - 1)
          : search.searchQuery;
      let isSanimpresaMember =
        getters["isSanimpresaMemberForBookableMonths"] || false;
      let doctors = await services.bookingsService.getDoctors({
        toSearch: search.searchQuery,
        officeServiceId: getters["getOffice"].officeId,
        province: getters["getProvince"].code,
        serviceId: getters["getService"].serviceId,
        ...getters["getConstraints"],
        isSanimpresaMember: isSanimpresaMember
      });
      let constraints = getters["getConstraints"];
      // Finds the doctors that are available for the selected service
      doctors = await findItemsFullfilingConstraints(doctors, constraints);
      commit("searchResults", { results: doctors });

      if (!search.autocomplete) {
        doctors = doctors.filter(doc =>
          doc.officeName.includes(searchedOfficeName)
        );
        // For each doctor get the relevant office
        for (let doctor of doctors) {
          let office = await services.bookingsService.getOfficeService({
            officeserviceId: doctor.officeServiceId
          });
          if (doctor.offices) doctor.offices.push(office);
          else doctor["office"] = office;
        }
        commit("doctors", doctors);
      }
    } catch (error) {
      commit("error/setError", error, { root: true });
    }
  },
  async getDoctorsByOfficeService({ commit, getters }) {
    try {
      let doctors = [];
      if (
        getters["getBond"] === "DATE" &&
        getters["getOffice"].bookingType === "SLOT"
      ) {
        let dateTime = getters["getSlot"].dateTime;
        let day = dayjs(dateTime).format("YYYY-MM-DD");
        let from = dayjs(dateTime).format("HH:mm");
        let to = null;
        let slotDuration = getters["getSlotDuration"];
        if (slotDuration && slotDuration > 0) {
          let toPlus = dayjs(dateTime).add(slotDuration.toString(), "minute");
          if (toPlus) {
            to = dayjs(toPlus).format("HH:mm");
          }
        }
        doctors = await services.bookingsService.callGetDoctorsByOfficeService({
          officeServiceId: getters["getOffice"].officeServiceId,
          day: day,
          fromTime: from + ":00",
          toTime: to ? to + ":00" : null
        });
      } else {
        doctors = await services.bookingsService.callGetDoctorsByOfficeService({
          officeServiceId: getters["getOffice"].officeServiceId
        });
      }
      commit("doctors", doctors);
    } catch (error) {
      commit("error/setError", error, { root: true });
    }
  },
  /**
   * Gets the offices according to province and serviceId for office name filtering
   */
  async getOffices({ commit, getters }) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let isSanimpresaMember =
        getters["isSanimpresaMemberForBookableMonths"] || false;
      let offices = await services.bookingsService.getOffices({
        province: getters["getProvince"].code,
        serviceId: getters["getService"].serviceId,
        ...getters["getConstraints"],
        isSanimpresaMember: isSanimpresaMember
      });
      offices = sortByKey(offices, "legalName");
      applyOfficesOrder(offices, isSanimpresaMember);
      commit("offices", offices);
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/closeLoadingDialog", null, { root: true });
    }
  },
  // Get the offices by ranking
  async getOfficesByRanking({ commit, getters }, { address, distance }) {
    try {
      commit("loading/openLoadingDialog", null, {
        root: true
      });
      let isSanimpresaMember =
        getters["isSanimpresaMemberForBookableMonths"] || false;
      let payload = {
        province: getters["getProvince"].code,
        serviceId: getters["getService"].serviceId,
        obese: getters["getConstraints"].obese,
        claustrophobia: getters["getConstraints"].claustrophobia,
        isSanimpresaMember: isSanimpresaMember
      };
      if (address?.geo) {
        let [lt, lng] = address.geo.split(",");
        payload["memberGeoLocationLat"] = lt;
        payload["memberGeoLocationLong"] = lng;
      }
      let offices = await services.bookingsService.getOfficesByRanking(payload);
      if (distance) {
        offices = offices.filter(office => office.distanceFrom < distance);
      }
      let orderedOffices = sortByKey(
        offices,
        distance ? "distanceFrom" : "hfrs",
        distance ? "asc" : "dsc"
      );
      commit("searchResults", {
        results: applyOfficesOrder(orderedOffices, isSanimpresaMember)
      });
      return;
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  // Reset offices
  async resetOffices({ commit }) {
    try {
      commit("loading/openLoadingDialog", null, {
        root: true
      });
      commit("searchResults", {
        results: sortByKey([], "distanceFrom", "asc")
      });
      return;
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  // Commits service and retrieves all relevant info about it
  async setService({ commit, getters }, result) {
    try {
      if (
        result.serviceId &&
        result.serviceId !== getters["getService"].serviceId
      ) {
        commit("loading/openLoadingDialog", null, {
          root: true
        });
        let allAvailServices = getters["getServices"];
        let availService = allAvailServices.filter(s => {
          return s.serviceId === result.serviceId;
        });
        commit("services", availService);
        commit("searchText", availService[0]?.officialNameByLang);
        let provinceCode = getters["getProvince"]
          ? getters["getProvince"].code
          : null;
        let payload = {
          serviceId: result.serviceId,
          provinceCode: provinceCode,
          ancillaryIds: []
        };
        let service = await services.bookingsService.getServiceWithId(payload);
        // Sets exemption
        commit(
          "exemption",
          service.exemption ? service.exemption.slice(4, -1) : 0
        );
        service["nameByLang"] = result.nameByLang;
        commit("service", { service });
      }
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, {
        root: true
      });
    }
  },
  // Sets the available months for the booking
  async calculateBookableMonthsWithBasedCriteria({ commit, getters }) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      commit("messages", []);
      let messages = [];
      let bookableMonths = [];
      let service = getters["getService"];
      let serviceId = service.serviceId;
      let serviceMinPrice = parseFloat(service.minPrice.slice(4, -1)) || 0;
      let serviceMaxPrice = parseFloat(service.maxPrice.slice(4, -1)) || 0;
      commit("loading/openLoadingDialog", null, { root: true });
      // call to get the booking info of the logged user for the given service
      let bookingInfo = await services.coverageService.getServiceBookingInfo({
        id: serviceId,
        memberFiscalCode: getters["getBeneficiary"].codiceFiscale
      });
      if (Object.keys(bookingInfo).length) {
        let thisYear = dayjs().year();
        /**
         * First step
         *  - check if the number of maximum booked for the current year is ok
         *  - check if the residual ceiling for the current year is ok
         * Only if both are ok:
         *  - build the bookable months for the current year
         *  - check the residual ceiling for the current year againts the service min and service max prices
         */
        let currentMaxAllowedHealthCare =
          bookingInfo[`${thisYear}`].residualCeiling.maxAllowedHealthCare;
        let currentResidualCeiling =
          bookingInfo[`${thisYear}`].residualCeiling.residualCeiling;
        if (currentMaxAllowedHealthCare <= 0 || currentResidualCeiling <= 0) {
          if (currentMaxAllowedHealthCare <= 0) {
            messages.push(
              "bookingWizard.serviceCoverageDialog.exceededThisYearHealthCare"
            );
          }
          if (currentResidualCeiling <= 0) {
            messages.push(
              "bookingWizard.serviceCoverageDialog.exceededThisYearCeilings"
            );
          }
        } else {
          // build the bookable/reminder months for the current year
          bookableMonths.push(
            ...bookingInfo[`${thisYear}`].coverage.map(month => ({
              year: thisYear,
              isBookable:
                month.bookableByRule &&
                !month.notCovered &&
                !month.maxCeilingReached &&
                !month.maxHealthCareReached,
              ...month
            }))
          );
          // check the residual ceiling for the current year againts the service min and service max prices
          let currentResidual = bookingInfo[`${thisYear}`].residualCeiling;
          let currentResidualCeiling = currentResidual.residualCeiling;
          if (
            currentResidualCeiling &&
            currentResidualCeiling < serviceMaxPrice &&
            currentResidualCeiling >= serviceMinPrice
          ) {
            messages.push(
              "bookingWizard.serviceCoverageDialog.currentYearResidualMinMax"
            );
          } else if (
            currentResidualCeiling &&
            currentResidualCeiling > 0 &&
            currentResidualCeiling < serviceMinPrice
          ) {
            messages.push(
              "bookingWizard.serviceCoverageDialog.currentYearResidualZeroMin"
            );
          }
        }
        /**
         * Second step
         *  - check if is possible to book for the next year
         * Only if is ok:
         *  - check if the number of maximum booked for the next year is ok
         *  - check if the residual ceiling for the next year is ok
         * Only if both are ok:
         *  - build the bookable months for the next year
         *  - check the residual ceiling for the next year againts the service min and service max prices
         */
        let nextYear = bookingInfo[`${thisYear + 1}`];
        if (nextYear) {
          let nextYearBookingIsPossible = nextYear.coverage;
          nextYearBookingIsPossible = nextYearBookingIsPossible.filter(
            coverage =>
              coverage.bookableByRule &&
              !coverage.notCovered &&
              !coverage.maxCeilingReached &&
              !coverage.maxHealthCareReached
          );
          if (nextYearBookingIsPossible.length > 0) {
            messages.push(
              "bookingWizard.serviceCoverageDialog.nextYearCanBook"
            );
            let nextMaxAllowedHealthCare =
              nextYear.residualCeiling.maxAllowedHealthCare;
            let nextResidualCeiling = nextYear.residualCeiling.residualCeiling;
            if (nextMaxAllowedHealthCare <= 0 || nextResidualCeiling <= 0) {
              if (nextMaxAllowedHealthCare <= 0) {
                messages.push(
                  "bookingWizard.serviceCoverageDialog.exceededNextYearHealthCare"
                );
              }
              if (nextResidualCeiling <= 0) {
                messages.push(
                  "bookingWizard.serviceCoverageDialog.exceededNextYearCeilings"
                );
              }
            } else {
              // build the bookable/reminder months for the next year
              bookableMonths.push(
                ...nextYear.coverage.map(month => ({
                  year: thisYear + 1,
                  isBookable:
                    month.bookableByRule &&
                    !month.notCovered &&
                    !month.maxCeilingReached &&
                    !month.maxHealthCareReached,
                  ...month
                }))
              );
              // check the residual ceiling for the next year againts the service min and service max prices
              let nextResidual = nextYear.residualCeiling;
              let nextResidualCeiling = nextResidual.residualCeiling;
              if (
                nextResidualCeiling &&
                nextResidualCeiling < serviceMaxPrice &&
                nextResidualCeiling >= serviceMinPrice
              ) {
                messages.push(
                  "bookingWizard.serviceCoverageDialog.nextYearResidualMinMax"
                );
              } else if (
                nextResidualCeiling &&
                nextResidualCeiling > 0 &&
                nextResidualCeiling < serviceMinPrice
              ) {
                messages.push(
                  "bookingWizard.serviceCoverageDialog.nextYearResidualZeroMin"
                );
              }
              messages.push(
                "bookingWizard.serviceCoverageDialog.nextYearDefaultMsg"
              );
            }
          } else {
            // build the reminder months for the next year
            bookableMonths.push(
              ...nextYear.coverage.map(month => ({
                year: thisYear + 1,
                isBookable:
                  month.bookableByRule &&
                  !month.notCovered &&
                  !month.maxCeilingReached &&
                  !month.maxHealthCareReached,
                ...month
              }))
            );
          }
        }
      }
      commit("messages", messages);
      let bookableMonthsGroupedByCodicePacchetto =
        groupBookableMonthsByCodicePacchetto(bookableMonths);
      let bm = bookableMonthsGroupedByCodicePacchetto.filter(
        month => month.isBookable
      );
      let rm = bookableMonthsGroupedByCodicePacchetto.filter(
        month => !month.isBookable
      );
      let bmes = bookableMonthsGroupedByCodicePacchetto.filter(
        month => month.isBookable && month.codicePacchetto.includes("FE1")
      );
      let sbm = bookableMonthsGroupedByCodicePacchetto.filter(
        month => month.isBookable && month.codicePacchetto.includes("SANI")
      );
      commit("bookableMonths", bm);
      commit("reminderMonths", rm);
      commit("bookableMonthsExcludingSanimpresa", bmes);
      commit("sanimpresaBookableMonths", sbm);
    } catch (error) {
      console.error(error);
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  // Sets the office and proceeds to the next step
  setOffice({ commit }, result) {
    commit("office", result);
    commit("nextStep");
  },
  // Sets the doctor and proceeds to the next step
  async setDoctor({ state, commit, getters }, doctor) {
    commit("doctor", doctor);
    try {
      if (doctor?.doctorId && state.bond === "DOCTOR") {
        commit("loading/openLoadingDialog", null, { root: true });
        commit("office", doctor.office);
      } else if (state.bond === "OFFICE" || !doctor.doctorId) {
        let office = await services.bookingsService.getOfficeService({
          officeserviceId: getters.getOffice.officeServiceId
        });
        commit("office", office);
      }
      commit("nextStep");
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async getSlots({ commit, getters }, monthOffset) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let res;
      let nextAvailableMonth;
      do {
        res = await services.bookingsService.getSlotsForMonths({
          monthOffset: monthOffset,
          officeserviceId:
            getters["getBond"] === "DOCTOR"
              ? getters["getDoctor"].officeServiceId
              : getters["getOffice"].officeServiceId,
          obese: getters["getConstraints"].obese,
          doctorId: getters["getDoctor"].doctorId || null,
          claustrophobia: getters["getConstraints"].claustrophobia,
          strictDoctorId: getters["getBond"] === "DOCTOR" ? true : false
        });
        if (res.slotList && res.slotList.length == 0) {
          let bookableMonths = getters["getBookableMonthsToUse"];
          let thisMonth = dayjs().set("date", 1);
          let requestedMonth = thisMonth.add(monthOffset, "month");
          nextAvailableMonth = bookableMonths.find(m =>
            requestedMonth.isBefore(dayjs(`${m.year}-${m.month}-01`))
          );
          monthOffset = nextAvailableMonth
            ? Math.abs(
                dayjs(
                  `${nextAvailableMonth.year}-${nextAvailableMonth.month}-01`
                ).month() - thisMonth.month()
              )
            : monthOffset;
          commit("slots", []);
        } else {
          commit("slotOffset", monthOffset);
          commit("slots", res.slotList);
          commit("slotDuration", res.duration.value);
        }
      } while (res.slotList.length === 0 && nextAvailableMonth !== undefined);
    } catch (error) {
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async getOperationalService({ commit, getters }) {
    let service = getters["getService"];
    let operationalService;
    try {
      if (service.operationalServiceId) {
        commit("loading/openLoadingDialog", null, { root: true });
        operationalService =
          await services.bookingsService.getOperationalService({
            officeId: getters["getOffice"].officeId,
            serviceId: service.operationalServiceId
          });
        commit("operationalService", operationalService);
        return operationalService;
      }
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async getMoreSlots({ commit, getters }) {
    commit("loading/openLoadingDialog", null, { root: true });
    commit("slotOffset", (getters["getSlotOffset"] || 0) + 1);
    let slots = await services.bookingsService.getSlotsForMonths({
      monthOffset: getters["getSlotOffset"] || 0,
      officeserviceId:
        getters["getBond"] === "DOCTOR"
          ? getters["getDoctor"].officeServiceId
          : getters["getOffice"].officeServiceId,
      obese: getters["getConstraints"].obese,
      doctorId: getters["getDoctor"].doctorId || null,
      claustrophobia: getters["getConstraints"].claustrophobia,
      strictDoctorId: getters["getBond"] === "DOCTOR"
    });
    commit("slots", [...getters["getSlots"], ...slots.slotList]);
    commit("slotDuration", slots.duration.value);
  },
  async computeCeiling({ commit, getters }) {
    commit("loading/openLoadingDialog", null, { root: true });
    let serviceId = getters["getService"]
      ? getters["getService"]?.serviceId
      : null;
    let paylod = {
      id: serviceId,
      year: new Date().getFullYear()
    };
    try {
      let ceiling = await services.bookingsService.getCeilingServiceWithId(
        paylod
      );
      commit("residualCeiling", ceiling);
      return ceiling;
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async findAvailableHospitalsBasedOnDate(
    { commit, getters },
    { address, distance }
  ) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let bookableMonths = getters["getBookableMonths"];
      let requestedDates = getRequestedDates(
        getters["getCustomSelectedDates"],
        getters["getMorningTimeFrom"],
        getters["getSplitTime"],
        getters["getAfternoonTimeTo"]
      );
      // isSanimpresaMember depends on dates requestedDates against the bookableMonths
      let isSanimpresaMember = getValueForSanimpresa(
        bookableMonths,
        requestedDates
      );
      let payload = {
        serviceId: getters["getService"].serviceId,
        obese: getters["getConstraints"].obese,
        claustrophobia: getters["getConstraints"].claustrophobia,
        requestDateWithTimes: requestedDates,
        isSanimpresaMember: isSanimpresaMember
      };
      if (address?.geo) {
        let [lt, lng] = address.geo.split(",");
        payload["memberGeoLocationLat"] = lt;
        payload["memberGeoLocationLong"] = lng;
      }
      let offices = [];
      if (distance) {
        payload["distanceInKm"] = distance;
        offices = await services.bookingsService.getOfficesByDateByDistance(
          payload
        );
        let keys = Object.keys(offices);
        for (let key of keys) {
          offices[key] = offices[key].filter(
            office => office.distanceFrom < distance
          );
          offices[key] = sortByKey(offices[key], "distanceFrom", "asc");
        }
      } else {
        payload["province"] = getters["getProvince"].code;
        offices = await services.bookingsService.getOfficesByDate(payload);
      }
      if (offices["hfOfficesSlotBased"]) {
        for (let office of offices["hfOfficesSlotBased"]) {
          let officeSlotList = [];
          let slotDuration = 0;
          let officeSlots = office.nestCalendar
            ? office.nestCalendar.servicePeriodSlot
              ? office.nestCalendar.servicePeriodSlot
              : []
            : [];
          let officeSlotsWeekDays = officeSlots.map(slot => slot.day);
          for (let requestDate of getRequestedDates(
            getters["getCustomSelectedDates"],
            getters["getMorningTimeFrom"],
            getters["getSplitTime"],
            getters["getAfternoonTimeTo"]
          )) {
            let day = dayjs(requestDate.day);
            let dayName = day.locale("en").format("dddd").toUpperCase();
            let todayPlus48Hours = dayjs().add(72, "hour"); // <-- dayjs starts from today
            let slotDayNotValid = day.isBefore(todayPlus48Hours, "date");
            if (!slotDayNotValid && officeSlotsWeekDays.includes(dayName)) {
              let res = await services.bookingsService.getSlots({
                from: `${requestDate.day}T${requestDate.fromTime}`,
                to: `${requestDate.day}T${requestDate.toTime}`,
                officeserviceId: office.officeServiceId,
                obese: getters["getConstraints"].obese,
                claustrophobia: getters["getConstraints"].claustrophobia,
                doctorId: getters["getDoctor"].doctorId || null,
                strictDoctorId: getters["getBond"] === "DOCTOR"
              });
              officeSlotList = officeSlotList.concat(res.slotList);
              // the slot duration is equal for every day, so we could take the last duration value as a valid value
              slotDuration = res.duration.value;
            }
            office["slots"] = officeSlotList;
            office["slotDuration"] = slotDuration;
          }
        }
      }
      // remove office without slots
      let slotOffices = offices["hfOfficesSlotBased"].filter(
        off => off["slots"].length > 0
      );
      offices["hfOfficesSlotBased"] = slotOffices;
      commit("officesByDate", offices);
    } catch (error) {
      // throw error;
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async askForCoverage({ commit, dispatch }, { month, year }) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      await services.bookingsService.askForCoverage({
        month,
        year
      });
      commit(
        "success/openSuccessDialog",
        {
          title: "dialog.success.askForCoverage.title",
          message: "dialog.success.askForCoverage.message",
          btnText: "exit",
          btnAction: () => {
            commit("success/reset", null, {
              root: true
            });
            dispatch("coverages/getCurrentMonths", 3, { root: true });
          }
        },
        { root: true }
      );
      return;
    } catch (error) {
      commit("error/setError", error, { root: true });
      throw error;
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async saveReminder({ commit, rootGetters, getters }, selectedDay) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      await services.bookingsService.createReminder({
        memberId: rootGetters["login/userInfo"].idIscritto,
        serviceId: getters["getService"].serviceId,
        provinceCode: getters["getProvince"].code,
        expectedDay: selectedDay,
        memorandumType: "APPOINTMENT",
        note: null,
        memberCodiceFiscale: rootGetters["login/userInfo"].codiceFiscale
      });
      return;
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  /**
   * Changes preferred contact channel in userInfo
   */
  async changePreferredContactChannel({ commit, getters }, channel) {
    let userInfo = { ...getters["getUserInfo"] };
    userInfo.preferenzaContatto = channel;
    commit("userInfo", userInfo);
    commit("setContactChannelChanged", true);
  },
  /**
   * Changes voucher mode in userInfo
   */
  async changeSendVoucherMode({ commit, getters }, voucherMode) {
    let userInfo = { ...getters["getUserInfo"] };
    userInfo.modalitaInvioVoucher = voucherMode;
    commit("userInfo", userInfo);
    commit("setSendVoucherModeChanged", true);
  },
  /**
   * Constructs the appointment request taking info from the steps of the wizard
   * @param {*}
   */
  async constructAppointment({ commit, getters, dispatch }) {
    commit("loading/openLoadingDialog", null, { root: true });
    let appointment = {};
    let province = { ...getters["getProvince"] };
    let userInfo = { ...getters["getUserInfo"] };
    let channel = userInfo.preferenzaContatto;
    let voucherMode = userInfo.modalitaInvioVoucher;
    let voucherChannelType = "NONE";
    if (voucherMode == "M") {
      voucherChannelType = "MAIL";
    } else if (voucherMode == "C") {
      voucherChannelType = "MAIL_ENCRYPTED";
    } else if (voucherMode == "S") {
      voucherChannelType = "SHORT_SMS";
    } else if (voucherMode == "L") {
      voucherChannelType = "LONG_SMS";
    }
    let voucherContact = {
      email: userInfo.email3,
      phone: userInfo.telefono3,
      voucherChannelType
    };
    delete province["covered"];
    delete province["bookable"];
    delete province["fullyCovered"];
    appointment["province"] = province;
    appointment["voucherContact"] = voucherContact;
    appointment["serviceId"] = getters["getService"].serviceId;
    appointment["hfOfficeId"] = getters["getOffice"].officeId;
    appointment["appointmentType"] = getters["getAppointmentType"];
    appointment["doctorId"] = getters["getDoctor"].doctorId || null;
    appointment["pathology"] = getters["getPathology"];
    let c = getters["getConstraints"];
    c["obesity"] = c["obese"]; // for the backend the property name is obesity e not obese
    delete c["obese"];
    appointment["constraint"] = c;
    appointment["appointmentFlowSearchType"] = getters["getBond"]
      ? "BY_" + getters["getBond"]
      : "NONE";
    if (getters["getAppointmentType"] === "SLOT") {
      let slot = getters["getSlot"];
      if (slot["dateTime"]) {
        appointment["appointmentDate"] = dayjs(slot["dateTime"]).format();
      }
    } else if (getters["getAppointmentType"] === "TIMEPERIOD") {
      let mappedDates = { ...getters["getPortalDays"] };
      let requestedDates = [];
      let dateKeys = Object.keys(mappedDates);
      dateKeys.forEach(dk => {
        mappedDates[dk].forEach(date => {
          date.timetables.forEach(timetable => {
            if (timetable.changed) {
              if (timetable.slots.length > 0) {
                let slots = timetable.slots.map(slot => {
                  let [from, to] = slot.split(" - ");
                  let fromTime = `${from}:00`;
                  let toTime = `${to}:00`;
                  return {
                    day: date.date,
                    fromTime: fromTime,
                    toTime: toTime
                  };
                });
                requestedDates.push(...slots);
              } else {
                let from = timetable.fromHHMM.substring(
                  0,
                  timetable.fromHHMM.lastIndexOf(":")
                );
                let to = timetable.toHHMM.substring(
                  0,
                  timetable.toHHMM.lastIndexOf(":")
                );
                let obj = {
                  day: date.date,
                  fromTime: `${from}:00`,
                  toTime: `${to}:00`
                };
                requestedDates.push(obj);
              }
            }
          });
        });
      });
      requestedDates.sort((a, b) => {
        let aDate = new Date(a.day + " " + a.fromTime);
        let bDate = new Date(b.day + " " + b.fromTime);
        return aDate.getTime() - bDate.getTime();
      });
      appointment["requestDateWithTimes"] = requestedDates;
      appointment["prestazioneInPrescrizione"] =
        getters["getServicePrescription"];
    }
    try {
      let isContactChannelChanged = getters["isContactChannelChanged"];
      if (isContactChannelChanged) {
        await dispatch("profile/changePreferredContactChannel", channel, {
          root: true
        });
      }
      let isSendVoucherModeChanged = getters["isSendVoucherModeChanged"];
      if (isSendVoucherModeChanged) {
        await dispatch("profile/changeSendVoucherMode", voucherMode, {
          root: true
        });
      }
      commit("loading/openLoadingDialog", null, { root: true });
      let a = await services.appointmentService.sendAppointment(appointment);
      commit("appointmentInfo", a);
      commit("appointmentId", a.appointmentId);
      commit("nextStep");
      return;
    } catch (error) {
      error.callback = function () {
        router.push({ name: "profile" });
      };
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async addReminder({ commit, getters }) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let createdAppId = getters["getCreatedAppointmentId"];
      if (createdAppId) {
        await services.appointmentService.setReminder(createdAppId);
      }
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async sendFeedBack({ commit, getters }, feedback) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let createdAppId = getters["getCreatedAppointmentId"];
      if (createdAppId) {
        return await services.bookingsService.sendFeedBack({
          appointmentId: createdAppId,
          feedback
        });
      }
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async resetAllPeriodSlots({ commit }) {
    await commit("resetAllPeriodSlots");
  },
  async setExpostObject({ commit, getters }) {
    commit("setExpostObject", getters["getBookableMonthsToUse"]);
  }
};

const sortByKey = (array, key, order) => {
  if (order === "asc" || !order) {
    return array.sort((a, b) => {
      let aVal = typeof a[key] === "string" ? a[key].toUpperCase() : a[key];
      let bVal = typeof b[key] === "string" ? b[key].toUpperCase() : b[key];
      if (aVal < bVal) return -1;
      else if (aVal > bVal) return 1;
      else return 0;
    });
  } else {
    return array.sort((a, b) => {
      let aVal = typeof a[key] === "string" ? a[key].toUpperCase() : a[key];
      let bVal = typeof b[key] === "string" ? b[key].toUpperCase() : b[key];
      if (aVal < bVal) return 1;
      else if (aVal > bVal) return -1;
      else return 0;
    });
  }
};

const resetSteps = (steps, start, end, bond, from) => {
  start = start || 0;
  end = end || steps.length;
  for (let i = start; i < end; i++) {
    let step = steps[i];
    step.active = false;
    step.visited = false;
    step.completed = false;
    step.selectedInfo = "";
    step.searchResults = [];
    step.searchText = "";
    Object.keys(step.info).forEach(key => {
      if (step.info[key]) {
        if (typeof step.info[key] === "object") {
          if (step.info[key].length === undefined) {
            step.info[key] = {};
          } else {
            step.info[key] = [];
          }
        } else if (typeof step.info[key] === "string") {
          step.info[key] = "";
        } else if (typeof step.info[key] === "number") {
          step.info[key] = 0;
        }
      }
    });
  }
  // only in this case reset the portale step to clean
  // the info properties for the wizard case:
  // DATE bond for a TIMEPERIOD appointment
  // that are filled by the portalDaysByPortal mutation
  if (start === 11 && end === 12) {
    let step = steps[12];
    resetPortaleStep(step);
  }
  // for DOCTOR bond...
  if (bond && bond === "DOCTOR") {
    // when reset the portale step or calendario step, reset also the struttra step
    if ((start === 12 && end === 13) || (start === 11 && end === 12)) {
      let step = steps[9];
      resetStep(step);
    }
  }
  // for DATE bond...
  if (bond && bond === "DATE") {
    // when back from the doctor step, reset also some properties of the calendario step
    if (start === 10 && end === 11) {
      let step = steps[11];
      step.visited = false;
      step.completed = false;
    }
    // when back from struttra step, reset also the "slot" property of the calendario step
    if (start === 9 && end === 10) {
      let step = steps[11];
      step.info.slot = "";
    }
  }
  // for OFFICE bond...
  if (bond && bond === "OFFICE") {
    // when go directly to service step clicking from side step menu icon, reset the previously choosed office
    if (start === 11 && end === 12 && from === "EDIT") {
      let step = steps[9];
      step.searchResults = [];
      step.info.office = {};
      step.info.officeServicePrice = 0;
      step.info.officeServiceExemption = 0;
      step.info.offices = [];
      step.info.officesByDate = {};
    }
    // when back from the portale step, reset also some properties of the doctor step
    if (start === 12 && end === 13) {
      let step = steps[10];
      step.completed = false;
      step.selectedInfo = "";
      step.info.doctor = {};
    }
  }
  return steps;
};

const resetStepsForEditAction = (steps, start, end) => {
  start = start || 0;
  end = end || steps.length;
  for (let i = start; i < end; i++) {
    let step = steps[i];
    step.active = false;
    step.visited = false;
    step.completed = false;
    step.selectedInfo = "";
    step.searchResults = [];
    step.searchText = "";
    Object.keys(step.info).forEach(key => {
      if (step.info[key]) {
        if (typeof step.info[key] === "object") {
          if (step.info[key].length === undefined) {
            step.info[key] = {};
          } else {
            step.info[key] = [];
          }
        } else if (typeof step.info[key] === "string") {
          step.info[key] = "";
        } else if (typeof step.info[key] === "number") {
          step.info[key] = 0;
        }
      }
    });
  }
  return steps;
};

const resetPortaleStep = step => {
  Object.keys(step.info).forEach(key => {
    if (step.info[key]) {
      if (typeof step.info[key] === "object") {
        if (step.info[key].length === undefined) {
          step.info[key] = {};
        } else {
          step.info[key] = [];
        }
      } else if (typeof step.info[key] === "string") {
        step.info[key] = "";
      } else if (typeof step.info[key] === "number") {
        step.info[key] = 0;
      }
    }
  });
};

const resetStep = step => {
  step.active = false;
  step.completed = false;
  step.visited = false;
  step.selectedInfo = "";
  step.searchResults = [];
  step.searchText = "";
  Object.keys(step.info).forEach(key => {
    if (step.info[key]) {
      if (typeof step.info[key] === "object") {
        if (step.info[key].length === undefined) {
          step.info[key] = {};
        } else {
          step.info[key] = [];
        }
      } else if (typeof step.info[key] === "string") {
        step.info[key] = "";
      } else if (typeof step.info[key] === "number") {
        step.info[key] = 0;
      }
    }
  });
};

/**
 * If sanimpresa member and a sanimpresa office exists, move it in first position
 * @param {*} offices The list of offices to reorder, if necessary
 * @param {*} isSanimpresaMember If is a sanimpresa member
 */
const applyOfficesOrder = (offices, isSanimpresaMember) => {
  // if sanimpresa member...
  if (isSanimpresaMember) {
    // ... if sanimpresa office exists...
    let sanimpresaOffice = offices.find(office => office.sanimpresa);
    if (sanimpresaOffice) {
      // ... move sanimpresa office on first position
      offices.unshift(
        offices.splice(
          offices.findIndex(office => office.sanimpresa),
          1
        )[0]
      );
      return offices;
    } else {
      return offices;
    }
  } else {
    return offices;
  }
};

const findItemsFullfilingConstraints = (array, constraints) => {
  return array.filter(item => {
    if (item.deliverRequestedService) {
      if (
        (constraints.obese && !item.isForObese) ||
        (constraints.claustrophobia && !item.isForClaustrophobic)
      ) {
        return false;
      } else return true;
    } else return false;
  });
};

const getRequestedDates = (
  dates,
  morningTimeFrom,
  splitTime,
  afternoonTimeTo
) => {
  let rdates = [...dates];
  return rdates.map(date => {
    let fromTime, toTime;
    if (date.morningTimes && date.afternoonTimes) {
      fromTime = morningTimeFrom;
      toTime = afternoonTimeTo;
    } else if (date.morningTimes) {
      fromTime = morningTimeFrom;
      toTime = splitTime;
    } else {
      fromTime = splitTime;
      toTime = afternoonTimeTo;
    }
    return {
      day: date.date,
      fromTime,
      toTime
    };
  });
};

const getValueForSanimpresa = (bookableMonths, requestedDates) => {
  // using Set to avoid duplicate months
  let requestedMonths = [
    ...new Set(
      requestedDates.map(obj => {
        return dayjs(obj.day).month() + 1; // dayjs is zero based
      })
    )
  ];
  return requestedMonths.some(m =>
    bookableMonths.some(
      obj => obj.month === m && obj.codicePacchetto.includes("SANI")
    )
  );
};

const getRequestedDatesFromPortalDays = mappedDates => {
  let requestedDates = [];
  let dateKeys = Object.keys(mappedDates);
  dateKeys.forEach(dk => {
    mappedDates[dk].forEach(date => {
      date.timetables.forEach(timetable => {
        if (timetable.changed) {
          if (timetable.slots.length > 0) {
            let slots = timetable.slots.map(slot => {
              let [from, to] = slot.split(" - ");
              let fromTime = `${from}:00`;
              let toTime = `${to}:00`;
              let dateFrom = dayjs(date.date)
                .hour(`${from.substring(0, from.lastIndexOf(":"))}`)
                .minute(`${from.substr(from.lastIndexOf(":") + 1)}`)
                .format("YYYY-MM-DD HH:mm");
              return {
                dateFrom: dateFrom,
                day: date.date,
                fromTime: fromTime,
                toTime: toTime
              };
            });
            requestedDates.push(...slots);
          } else {
            let from = timetable.fromHHMM.substring(
              0,
              timetable.fromHHMM.lastIndexOf(":")
            );
            let to = timetable.toHHMM.substring(
              0,
              timetable.toHHMM.lastIndexOf(":")
            );
            let dateFrom = dayjs(date.date)
              .hour(`${from.substring(0, from.lastIndexOf(":"))}`)
              .minute(`${from.substr(from.lastIndexOf(":") + 1)}`)
              .format("YYYY-MM-DD HH:mm");
            let obj = {
              dateFrom: dateFrom,
              day: date.date,
              fromTime: `${from}:00`,
              toTime: `${to}:00`
            };
            requestedDates.push(obj);
          }
        }
      });
    });
  });
  requestedDates.sort((a, b) =>
    dayjs(a.dateFrom).isAfter(dayjs(b.dateFrom)) ? 1 : -1
  );
  return requestedDates;
};

const requestedDatesGrouppedByDate = requestedDates => {
  if (requestedDates) {
    return requestedDates.reduce((acc, d) => {
      let found = acc.find(a => a.day === d.day);
      let from = d.fromTime.substring(0, d.fromTime.lastIndexOf(":"));
      let to = d.toTime
        ? d.toTime.substring(0, d.toTime.lastIndexOf(":"))
        : null;
      let value = to ? from.concat(" - ").concat(to) : from;
      if (!found) {
        acc.push({ day: d.day, slots: [value] });
      } else {
        found.slots.push(value);
      }
      return acc;
    }, []);
  }
  return null;
};

const compressedRequestedDates = grouppedByDate => {
  if (grouppedByDate) {
    let result = [];
    grouppedByDate.forEach(d => {
      let compressedSlots = d.slots.reduce((acc, slot) => {
        if (acc.length === 0) {
          acc.push(slot);
        } else {
          let curr = slot.split(" - ");
          let prev = acc[acc.length - 1].split(" - ");
          if (prev[1] !== curr[0]) {
            acc.push(slot);
          } else {
            prev[1] = curr[1];
            acc[acc.length - 1] = prev.join(" - ");
          }
        }
        return acc;
      }, []);
      let day = dayjs(d.day)
        .locale(i18n ? i18n.locale : "it")
        .format("YYYY-MM-DD");
      let dayToView = dayjs(d.day)
        .locale(i18n ? i18n.locale : "it")
        .format("DD MMMM YYYY");
      compressedSlots.forEach(s => {
        let fromTo = s.split(" - ");
        result.push({
          day: day,
          dayToView: dayToView,
          from: fromTo[0],
          to: fromTo[1]
        });
      });
    });
    return result;
  }
  return null;
};

const compressedAsText = compressed => {
  if (compressed) {
    let dateString = "";
    let from = i18n ? i18n.t("fromTime") : "dalle";
    let to = i18n ? i18n.t("toTime") : "alle";
    compressed.forEach(d => {
      if (d.to) {
        dateString += `${d.dayToView} ${from} ${d.from} ${to} ${d.to}`;
      } else {
        dateString += `${d.dayToView} ${from} ${d.from}`;
      }
      dateString += "</br>";
    });
    return dateString;
  }
  return null;
};

function groupBookableMonthsByCodicePacchetto(bookableMonths) {
  let firstMonth = bookableMonths.slice(0, 1)[0];
  let startYear = firstMonth.year;
  let startMonth =
    firstMonth.month.length === 1 ? `0${firstMonth.month}` : firstMonth.month;
  let lastMonth = bookableMonths.slice(-1)[0];
  let endYear = lastMonth.year;
  let endMonth =
    lastMonth.month.length === 1 ? `0${lastMonth.month}` : lastMonth.month;
  let from = dayjs(`${startYear}-${startMonth}-01`);
  let to = dayjs(`${endYear}-${endMonth}-01`);
  let months = [];
  let monthDate = from;
  while (monthDate.isSame(to) || monthDate.isBefore(to)) {
    let searchedMonths = bookableMonths.filter(
      coverage =>
        monthDate.year() === coverage.year &&
        monthDate.month() === coverage.month - 1
    );
    if (searchedMonths.length > 0) {
      let result = searchedMonths.reduce((acc, curr) => {
        acc.bookableByRule = curr.bookableByRule;
        acc.codicePacchetto = curr.codicePacchetto;
        acc.healthCareResidual = curr.healthCareResidual;
        acc.isBookable = curr.isBookable;
        acc.maxCeilingReached = curr.maxCeilingReached;
        acc.maxHealthCareReached = curr.maxHealthCareReached;
        acc.month = curr.month;
        acc.notCovered = curr.notCovered;
        acc.year = curr.year;
        return acc;
      }, {});
      if (!result.codicePacchetto.includes("FE1")) {
        result["notCovered"] = true;
        result["isBookable"] = false;
      }
      months.push(result);
    } else {
      months.push({
        bookableByRule: false,
        codicePacchetto: [],
        healthCareResidual: 0,
        isBookable: false,
        maxCeilingReached: true,
        maxHealthCareReached: true,
        month: monthDate.month() + 1,
        notCovered: true,
        year: monthDate.year()
      });
    }
    monthDate = monthDate.add(1, "month");
  }
  return months;
}

export default {
  namespaced: true,
  strict,
  state,
  getters,
  mutations,
  actions
};
